import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Trash2, Plus, Minus, ShoppingCart, MapPin, CreditCard,
  Banknote, ArrowLeft, Tag, CheckCircle, Crosshair, Navigation,
  AlertCircle, X, Edit3, Package
} from 'lucide-react';
import Navbar from '../components/Navbar';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useLocation2 } from '../context/LocationContext';
import API from '../utils/api';
import { toast } from 'react-toastify';

const DEFAULT_ADVANCE = 499;

const variantLabel = (v) => {
  if (!v) return null;
  if (typeof v === 'object') return v.size || null;
  return v;
};

// ── GPS Detector for delivery ─────────────────────────────────────────────
function DeliveryLocationPicker({ onSelect }) {
  const [gpsStatus, setGpsStatus] = useState('idle');
  const [gpsError, setGpsError] = useState('');
  const [mode, setMode] = useState(null); // null | 'gps' | 'manual'

  const detectGPS = () => {
    if (!navigator.geolocation) {
      setGpsError('GPS not supported by your browser. Please enter address manually.');
      setGpsStatus('error');
      return;
    }
    setGpsStatus('loading');
    setGpsError('');
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng, accuracy } = pos.coords;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
          );
          const data = await res.json();
          const addr = data.address || {};
          const location = {
            lat, lng,
            street: [addr.road, addr.neighbourhood, addr.suburb].filter(Boolean).join(', '),
            city: addr.city || addr.town || addr.village || addr.county || '',
            state: addr.state || '',
            pincode: addr.postcode || '',
            display: data.display_name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`
          };
          setGpsStatus('success');
          onSelect(location);
          toast.success('📍 Delivery location detected via GPS!', { autoClose: 2000 });
        } catch {
          const location = { lat, lng, street: '', city: '', state: '', pincode: '', display: `${lat.toFixed(4)}, ${lng.toFixed(4)}` };
          setGpsStatus('success');
          onSelect(location);
          toast.info('GPS detected — please verify City, State, Pincode below', { autoClose: 3000 });
        }
      },
      (err) => {
        setGpsStatus('error');
        if (err.code === 1) setGpsError('Location permission denied. Please allow access or enter manually.');
        else if (err.code === 2) setGpsError('Location unavailable. Please enter address manually.');
        else setGpsError('Location timeout. Please enter address manually.');
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  };

  if (mode === null) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* GPS Option */}
      <button onClick={() => { setMode('gps'); detectGPS(); }}
        style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'linear-gradient(135deg,#1A73E8,#0D62C9)', color: 'white', border: 'none', borderRadius: 12, padding: '14px 18px', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'left' }}>
        <div style={{ width: 40, height: 40, background: 'rgba(255,255,255,0.2)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Crosshair size={20} />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Use My Current Location</div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>Auto-detect via GPS · Most accurate</div>
        </div>
      </button>

      {/* Manual Option */}
      <button onClick={() => setMode('manual')}
        style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'white', border: '2px solid var(--gray-border)', borderRadius: 12, padding: '14px 18px', cursor: 'pointer', fontFamily: 'var(--font-body)', textAlign: 'left', color: 'var(--dark)' }}>
        <div style={{ width: 40, height: 40, background: 'var(--gray-light)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Edit3 size={20} color="var(--orange)" />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Enter Address Manually</div>
          <div style={{ fontSize: 12, color: 'var(--gray)' }}>Type your street, city, pincode</div>
        </div>
      </button>
    </div>
  );

  if (mode === 'gps') return (
    <div>
      {gpsStatus === 'loading' && (
        <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 12, padding: '16px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div className="spinner" style={{ width: 24, height: 24, borderWidth: 3, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: '#1D4ED8' }}>Detecting your location...</div>
            <div style={{ fontSize: 12, color: '#3B82F6' }}>Please allow location access if prompted</div>
          </div>
        </div>
      )}
      {gpsStatus === 'error' && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 12, padding: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12 }}>
            <AlertCircle size={16} color="var(--red)" style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 13, color: 'var(--red)' }}>{gpsError}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={detectGPS} style={{ flex: 1, padding: '8px', borderRadius: 8, border: '1.5px solid var(--orange)', background: 'white', color: 'var(--orange)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Try Again</button>
            <button onClick={() => setMode('manual')} style={{ flex: 1, padding: '8px', borderRadius: 8, border: 'none', background: 'var(--orange)', color: 'white', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Enter Manually</button>
          </div>
        </div>
      )}
      {gpsStatus === 'success' && (
        <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <CheckCircle size={16} color="var(--green)" />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--green)' }}>GPS location detected! Verify details below.</span>
        </div>
      )}
    </div>
  );

  return null;
}

// ── Main Cart Component ───────────────────────────────────────────────────
export default function Cart() {
  const { cart, cartOwner, removeFromCart, updateQuantity, clearCart, total } = useCart();
  const { user } = useAuth();
  const { location: savedLocation } = useLocation2();
  const navigate = useNavigate();

  const [payMethod, setPayMethod] = useState('cash');
  const [address, setAddress] = useState({
    street: user?.address?.street || savedLocation?.street || '',
    city: user?.address?.city || savedLocation?.city || '',
    state: user?.address?.state || savedLocation?.state || '',
    pincode: user?.address?.pincode || savedLocation?.pincode || '',
    lat: savedLocation?.lat || null,
    lng: savedLocation?.lng || null,
  });
  const [locMode, setLocMode] = useState(
    (user?.address?.city || savedLocation?.city) ? 'filled' : 'pick'
  );
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [advanceSetting, setAdvanceSetting] = useState(DEFAULT_ADVANCE);
  const [publicSettings, setPublicSettings] = useState({ delivery: {}, tax: {}, coupons: [] });
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [payNowMode, setPayNowMode] = useState('platform');

  useEffect(() => {
    API.get('/admin/public-settings')
      .then(res => {
        setAdvanceSetting(Number(res.data.payment?.advanceAmount) || DEFAULT_ADVANCE);
        setPublicSettings({
          delivery: res.data.delivery || {},
          tax: res.data.tax || {},
          coupons: res.data.coupons || []
        });
      })
      .catch(() => {});
  }, []);

  const platformFee = Math.max(0, advanceSetting);
  const deliveryFee = publicSettings.delivery?.enabled === false || (Number(publicSettings.delivery?.freeAbove) > 0 && total >= Number(publicSettings.delivery.freeAbove))
    ? 0
    : Math.max(0, Number(publicSettings.delivery?.baseFee) || 0);
  const taxAmount = publicSettings.tax?.enabled ? Math.round((total * (Number(publicSettings.tax?.percent) || 0)) / 100) : 0;
  const couponDiscount = appliedCoupon
    ? Math.min(
        appliedCoupon.type === 'percent' ? Math.round((total * Number(appliedCoupon.value || 0)) / 100) : Number(appliedCoupon.value || 0),
        total + deliveryFee + taxAmount
      )
    : 0;
  const orderTotal = Math.max(0, total + platformFee + deliveryFee + taxAmount - couponDiscount);
  const advance = payNowMode === 'full' ? orderTotal : platformFee;
  const remaining = Math.max(0, orderTotal - advance);

  const applyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    const coupon = publicSettings.coupons?.find(c => c.active !== false && c.code?.toUpperCase() === code);
    if (!coupon) {
      setAppliedCoupon(null);
      toast.error('Invalid coupon code');
      return;
    }
    if (total < Number(coupon.minOrder || 0)) {
      setAppliedCoupon(null);
      toast.error(`Minimum materials order is ₹${Number(coupon.minOrder).toLocaleString()}`);
      return;
    }
    setAppliedCoupon(coupon);
    setCouponInput(code);
    toast.success('Coupon applied');
  };

  const handleLocationDetected = (loc) => {
    setAddress(prev => ({
      ...prev,
      street: loc.street || prev.street,
      city: loc.city || prev.city,
      state: loc.state || prev.state,
      pincode: loc.pincode || prev.pincode,
      lat: loc.lat || prev.lat,
      lng: loc.lng || prev.lng,
    }));
    setLocMode('filled');
  };

  const confirmOrderSuccess = () => {
    clearCart();
    setOrderPlaced(true);
    setTimeout(() => navigate('/orders'), 2200);
  };

  const placeOrder = async () => {
    if (!address.city?.trim() || !address.state?.trim() || !address.pincode?.trim()) {
      toast.error('Please fill City, State and Pincode for delivery');
      return;
    }
    if (!/^\d{6}$/.test(address.pincode.trim())) {
      toast.error('Please enter a valid 6-digit pincode');
      return;
    }
    if (!address.street?.trim() && !(address.lat && address.lng)) {
      toast.error('Please enter a street or landmark, or select your GPS location');
      return;
    }
    setLoading(true);
    try {
      const orderRes = await API.post('/orders', {
        ownerId: cartOwner._id,
        items: cart.map(i => ({
          material: i.material._id,
          name: i.name,
          category: i.category,
          variant: variantLabel(i.variant),
          quantity: i.quantity,
          price: i.price,
          unit: i.unit
        })),
        deliveryAddress: address,
        paymentMethod: { advance: 'razorpay', remaining: payMethod },
        notes,
        couponCode: appliedCoupon?.code || '',
        payNowAmount: advance
      });
      const order = orderRes.data;

      const payRes = await API.post('/payment/create-order', { orderId: order._id, amount: advance });
      const { orderId: rzpOrderId, key, testMode, amount: rzpAmount, merchantName } = payRes.data;

      if (testMode || !key || key === 'rzp_test_placeholder') {
        await API.post('/payment/confirm-test', { orderId: order._id });
        confirmOrderSuccess();
        return;
      }

      if (!window.Razorpay) { toast.error('Razorpay not loaded. Please refresh.'); return; }

      new window.Razorpay({
        key, amount: rzpAmount, currency: 'INR',
        name: merchantName || 'Material Stock Admin',
        description: `Advance #${order._id.slice(-6).toUpperCase()}`,
        order_id: rzpOrderId,
        handler: async (response) => {
          try {
            await API.post('/payment/verify', {
              orderId: order._id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpayOrderId: response.razorpay_order_id,
              razorpaySignature: response.razorpay_signature
            });
            confirmOrderSuccess();
          } catch { toast.error('Payment verification failed'); }
        },
        prefill: { name: user.name, email: user.email, contact: user.phone },
        theme: { color: getComputedStyle(document.documentElement).getPropertyValue('--orange').trim() || '#15803D' },
        modal: { ondismiss: () => { toast.warning('Payment cancelled. Order saved.'); navigate('/orders'); } }
      }).open();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to place order');
    } finally { setLoading(false); }
  };

  // ── Success screen
  if (orderPlaced) return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="page-with-bottom-nav">
      <div style={{ textAlign: 'center', padding: 32 }} className="fade-in">
        <div style={{ width: 80, height: 80, background: '#ECFDF5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
          <CheckCircle size={48} color="var(--green)" />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Order Placed! 🎉</h2>
        <p style={{ color: 'var(--gray)', fontSize: 14 }}>Redirecting to your orders...</p>
      </div>
    </div>
  );

  // ── Empty cart
  if (cart.length === 0) return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }} className="page-with-bottom-nav">
      <Navbar />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', gap: 14, padding: 24 }}>
        <ShoppingCart size={64} color="var(--gray-border)" />
        <h2 style={{ fontSize: 20, fontWeight: 700 }}>Your cart is empty</h2>
        <p style={{ color: 'var(--gray)', fontSize: 14, textAlign: 'center' }}>Add materials from a shop to get started</p>
        <Link to="/shops" className="btn-primary" style={{ padding: '12px 28px', marginTop: 4 }}>Browse Shops</Link>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--page-bg)' }} className="page-with-bottom-nav">
      <Navbar />
      <div style={{ maxWidth: 680, margin: '0 auto', padding: '20px 16px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Link to="/shops" style={{ color: 'var(--gray)', display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
            <ArrowLeft size={16} /> Back
          </Link>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>My Cart</h1>
          <span style={{ background: 'var(--orange)', color: 'white', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
            {cart.length}
          </span>
        </div>

        {/* Shop Info */}
        <div style={{ background: 'white', borderRadius: 12, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, boxShadow: 'var(--shadow)' }}>
          <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg,var(--orange),var(--amber))', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
            {cartOwner?.shopName?.[0]}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{cartOwner?.shopName}</div>
            <div style={{ color: 'var(--gray)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
              <MapPin size={10} /> {cartOwner?.location?.city}
            </div>
          </div>
        </div>

        {/* Cart Items */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {cart.map(item => {
            const availableStock = Number(item.variant?.stock ?? item.material?.stock);
            const atStockLimit = Number.isFinite(availableStock) && item.quantity >= availableStock;
            return (
            <div key={item.key} className="cart-item-row" style={{ background: 'white', borderRadius: 12, padding: '12px 14px', boxShadow: 'var(--shadow)' }}>
              <div style={{ width: 42, height: 42, background: 'var(--orange-soft)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                {item.material?.images?.[0]
                  ? <img src={item.material.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }} />
                  : <Package size={18} color="var(--orange)" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                {variantLabel(item.variant) && (
                  <div style={{ color: 'var(--gray)', fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Tag size={9} /> {variantLabel(item.variant)}
                  </div>
                )}
                <div style={{ color: 'var(--orange)', fontWeight: 600, fontSize: 12 }}>
                  ₹{Number(item.price).toLocaleString()} / {item.unit}
                </div>
              </div>
              <div className="cart-qty" style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                <button onClick={() => updateQuantity(item.key, item.quantity - 1)}
                  aria-label={`Decrease quantity for ${item.name}`}
                  style={{ width: 26, height: 26, borderRadius: 7, border: '1.5px solid var(--gray-border)', background: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Minus size={11} />
                </button>
                <span style={{ fontWeight: 700, fontSize: 14, minWidth: 20, textAlign: 'center' }}>{item.quantity}</span>
                <button onClick={() => updateQuantity(item.key, item.quantity + 1)} disabled={atStockLimit}
                  aria-label={`Increase quantity for ${item.name}`}
                  title={atStockLimit ? `Only ${availableStock} available` : 'Increase quantity'}
                  style={{ width: 26, height: 26, borderRadius: 7, border: `1.5px solid ${atStockLimit ? 'var(--gray-border)' : 'var(--orange)'}`, background: atStockLimit ? 'var(--gray-light)' : 'var(--orange)', color: atStockLimit ? 'var(--gray)' : 'white', cursor: atStockLimit ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Plus size={11} />
                </button>
              </div>
              <div className="cart-line-total" style={{ fontWeight: 700, fontSize: 14, flexShrink: 0, minWidth: 58, textAlign: 'right' }}>
                ₹{(Number(item.price) * item.quantity).toLocaleString()}
              </div>
              <button onClick={() => removeFromCart(item.key)}
                className="cart-remove"
                aria-label={`Remove ${item.name} from cart`}
                style={{ background: '#FEF2F2', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', color: 'var(--red)', display: 'flex', flexShrink: 0 }}>
                <Trash2 size={14} />
              </button>
            </div>
          );})}
        </div>

        {/* ── DELIVERY ADDRESS SECTION ── */}
        <div style={{ background: 'white', borderRadius: 14, padding: '18px 16px', boxShadow: 'var(--shadow)', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 style={{ fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 7 }}>
              <MapPin size={15} color="var(--orange)" /> Delivery Address
            </h3>
            {locMode === 'filled' && (
              <button onClick={() => setLocMode('pick')}
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: '1.5px solid var(--gray-border)', borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: 'var(--dark)' }}>
                <Crosshair size={12} /> Change
              </button>
            )}
          </div>

          {/* Location Picker */}
          {locMode === 'pick' && (
            <div style={{ marginBottom: 16 }}>
              <DeliveryLocationPicker onSelect={handleLocationDetected} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 0', color: 'var(--gray)', fontSize: 12 }}>
                <div style={{ flex: 1, height: 1, background: 'var(--gray-border)' }} />
                or fill address below
                <div style={{ flex: 1, height: 1, background: 'var(--gray-border)' }} />
              </div>
            </div>
          )}

          {/* GPS detected banner */}
          {locMode === 'filled' && address.lat && address.lng && (
            <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 9, padding: '8px 12px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 7, fontSize: 12 }}>
              <Navigation size={13} color="var(--green)" />
              <span style={{ color: 'var(--green)', fontWeight: 600 }}>GPS coordinates saved</span>
              <span style={{ color: 'var(--gray)', marginLeft: 4 }}>· {Number(address.lat).toFixed(4)}, {Number(address.lng).toFixed(4)}</span>
            </div>
          )}

          {/* Address Fields — always visible */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input className="form-input" placeholder="Street / Area / Landmark"
              value={address.street}
              onChange={e => setAddress(a => ({ ...a, street: e.target.value }))} />
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" placeholder="City *"
                value={address.city}
                onChange={e => setAddress(a => ({ ...a, city: e.target.value }))}
                style={{ flex: 2 }} />
              <input className="form-input" placeholder="State *"
                value={address.state}
                onChange={e => setAddress(a => ({ ...a, state: e.target.value }))}
                style={{ flex: 2 }} />
              <input className="form-input" placeholder="Pincode *"
                value={address.pincode}
                onChange={e => setAddress(a => ({ ...a, pincode: e.target.value }))}
                style={{ flex: 1, minWidth: 80 }} />
            </div>
            <textarea className="form-input" rows={2}
              placeholder="Special delivery instructions (optional)..."
              value={notes} onChange={e => setNotes(e.target.value)} style={{ resize: 'none' }} />
          </div>
        </div>

        {/* Order Summary */}
        <div style={{ background: 'white', borderRadius: 14, padding: '16px', boxShadow: 'var(--shadow)' }}>
          <h3 style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>Order Summary</h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12, paddingBottom: 12, borderBottom: '1px dashed var(--gray-border)' }}>
            {cart.map(item => (
              <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--gray)', flex: 1, marginRight: 8 }}>
                  {item.name}{variantLabel(item.variant) ? ` (${variantLabel(item.variant)})` : ''} × {item.quantity}
                </span>
                <span style={{ fontWeight: 600 }}>₹{(Number(item.price) * item.quantity).toLocaleString()}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, paddingTop: 8, borderTop: '1px solid var(--gray-border)', marginTop: 4 }}>
              <span style={{ color: 'var(--gray)' }}>Materials Subtotal</span><span style={{ fontWeight: 600 }}>₹{total.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--gray)' }}>Platform / Advance Fee</span><span style={{ fontWeight: 600 }}>₹{platformFee.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--gray)' }}>Delivery Fee</span><span style={{ fontWeight: 600 }}>₹{deliveryFee.toLocaleString()}</span>
            </div>
            {taxAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--gray)' }}>Tax</span><span style={{ fontWeight: 600 }}>₹{taxAmount.toLocaleString()}</span>
              </div>
            )}
            {couponDiscount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: 'var(--green)' }}>Coupon {appliedCoupon?.code}</span><span style={{ fontWeight: 700, color: 'var(--green)' }}>-₹{couponDiscount.toLocaleString()}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15, paddingTop: 8, borderTop: '1px solid var(--gray-border)', marginTop: 4 }}>
              <span>Total Amount</span><span>₹{orderTotal.toLocaleString()}</span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <input className="form-input" placeholder="Coupon code"
              value={couponInput}
              onChange={e => setCouponInput(e.target.value.toUpperCase())}
              style={{ flex: 1, textTransform: 'uppercase' }} />
            <button onClick={applyCoupon} className="btn-outline" style={{ padding: '8px 14px', fontSize: 13 }}>
              Apply
            </button>
            {appliedCoupon && (
              <button onClick={() => { setAppliedCoupon(null); setCouponInput(''); }} style={{ width: 38, border: 'none', borderRadius: 8, background: '#FEF2F2', color: 'var(--red)', cursor: 'pointer' }}>
                <X size={15} />
              </button>
            )}
          </div>

          {/* Payment Breakdown */}
          <div style={{ background: 'var(--orange-soft)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--orange)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Payment Breakdown</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8, marginBottom: 12 }}>
              {[
                { key: 'platform', title: 'Only Platform Fee', amount: platformFee, sub: `Balance Rs ${Math.max(0, orderTotal - platformFee).toLocaleString()} later` },
                { key: 'full', title: 'Full Amount', amount: orderTotal, sub: 'No balance after payment' },
              ].map(opt => (
                <button key={opt.key} type="button" onClick={() => setPayNowMode(opt.key)}
                  style={{ textAlign: 'left', border: `2px solid ${payNowMode === opt.key ? 'var(--orange)' : 'var(--orange-border)'}`, background: payNowMode === opt.key ? 'white' : 'rgba(255,255,255,0.55)', borderRadius: 10, padding: '10px 11px', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 800, fontSize: 13 }}>{opt.title}</span>
                    {opt.key === 'platform' && <span style={{ background: 'var(--orange)', color: 'white', borderRadius: 4, padding: '1px 5px', fontSize: 9, fontWeight: 800 }}>REQUIRED</span>}
                  </div>
                  <div style={{ fontWeight: 900, color: 'var(--orange)', fontSize: 17 }}>Rs {opt.amount.toLocaleString()}</div>
                  <div style={{ color: 'var(--gray)', fontSize: 11 }}>{opt.sub}</div>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5, alignItems: 'center' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                Pay Now ({payNowMode === 'full' ? 'Full Amount' : 'Platform Fee'})
                <span style={{ background: 'var(--orange)', color: 'white', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 700 }}>REQUIRED</span>
              </span>
              <span style={{ fontWeight: 800, color: 'var(--orange)', fontSize: 15 }}>Rs {advance.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <span style={{ color: 'var(--gray)' }}>{remaining > 0 ? 'Balance on Delivery' : 'Balance After Payment'}</span>
              <span style={{ fontWeight: 700 }}>Rs {remaining.toLocaleString()}</span>
            </div>
          </div>

          {/* Remaining Payment Method */}
          {remaining > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Pay remaining on delivery by</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ key: 'cash', label: 'Cash', icon: Banknote }, { key: 'razorpay', label: 'Online', icon: CreditCard }].map(opt => (
                  <button key={opt.key} onClick={() => setPayMethod(opt.key)}
                    style={{ flex: 1, padding: '10px 8px', borderRadius: 10, border: `2px solid ${payMethod === opt.key ? 'var(--orange)' : 'var(--gray-border)'}`, background: payMethod === opt.key ? 'var(--orange-soft)' : 'white', color: payMethod === opt.key ? 'var(--orange)' : 'var(--dark)', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <opt.icon size={15} /> {opt.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Place Order */}
          <button onClick={placeOrder} className="btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: '14px', fontSize: 15 }}
            disabled={loading}>
            <CreditCard size={16} />
            {loading ? 'Placing Order...' : `Confirm Order - Pay Rs ${advance.toLocaleString()} ${payNowMode === 'full' ? 'Total' : 'Platform Fee'}`}
          </button>
          <p style={{ textAlign: 'center', color: 'var(--gray)', fontSize: 11, marginTop: 8 }}>
            Secured payment - Platform fee is compulsory{remaining > 0 ? ` - Balance Rs ${remaining.toLocaleString()} on delivery` : ' - No balance after payment'}
          </p>
        </div>
      </div>
    </div>
  );
}
